import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaComments,
  FaEnvelope,
  FaEnvelopeOpen,
  FaClock,
  FaReply,
  FaTrash,
  FaHome,
  FaArrowLeft,
  FaSpinner,
  FaPaperPlane,
} from 'react-icons/fa';
import HostHeader from '../components/HostHeader';
import { getApiUrl } from '../config/api';
import { toast } from 'react-toastify';
import {
  fetchHostMessages,
  fetchConversation,
  sendHostToGuestMessage,
  markMessageAsRead,
  deleteMessageApi,
} from '../services/messagingService';
import './HostMessagesPage.css';

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'G';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
};

const HostMessagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserKey, setSelectedUserKey] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [conversationThread, setConversationThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const chatBodyRef = useRef(null);

  const hostId = currentUser?._id || currentUser?.id;
  const hostName = currentUser?.name || currentUser?.email || 'Host';

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        setCurrentUser(JSON.parse(stored));
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  useEffect(() => {
    if (!hostId) return;
    loadMessages();
  }, [hostId]);

  useEffect(() => {
    if (!hostId) return;
    const token = localStorage.getItem('token');
    fetch(`${getApiUrl('/api/notifications/my-notifications')}?userType=host&unreadOnly=true`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.notifications)) {
          setUnreadNotifCount(data.unreadCount ?? data.notifications.length);
        }
      })
      .catch(() => {});
  }, [hostId]);

  // Group by user only: same user = one box (all their bookings/conversations together)
  const conversations = React.useMemo(() => {
    const map = new Map();
    (messages || []).forEach((m) => {
      const k = String(m.fromId || '');
      if (!map.has(k)) {
        map.set(k, {
          key: k,
          fromId: m.fromId,
          fromName: m.fromName,
          threads: new Map(),
          unreadCount: 0,
        });
      }
      const conv = map.get(k);
      const bid = String(m.bookingId || '');
      if (!conv.threads.has(bid)) {
        conv.threads.set(bid, { bookingId: m.bookingId, propertyName: m.propertyName, messages: [] });
      }
      const thread = conv.threads.get(bid);
      thread.messages.push(m);
      thread.messages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      thread.lastTime = thread.messages[0]?.timestamp;
      if (!m.read) conv.unreadCount += 1;
    });
    return Array.from(map.values()).map((conv) => {
      conv.threads = Array.from(conv.threads.values()).sort(
        (a, b) => new Date(b.lastTime || 0) - new Date(a.lastTime || 0)
      );
      const allMessages = conv.threads.flatMap((t) => t.messages);
      allMessages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      conv.lastMessage = allMessages[0];
      conv.lastTime = conv.lastMessage?.timestamp;
      conv.hasUnread = conv.unreadCount > 0;
      return conv;
    }).sort((a, b) => new Date(b.lastTime || 0) - new Date(a.lastTime || 0));
  }, [messages]);

  // Derived: current thread context for API (reply, load thread, booking details)
  const selectedMessage = React.useMemo(() => {
    if (!selectedUserKey || !selectedBookingId) return null;
    const conv = conversations.find((c) => c.key === selectedUserKey);
    if (!conv) return null;
    const thread = conv.threads.find((t) => String(t.bookingId) === String(selectedBookingId));
    if (!thread) return null;
    return {
      fromId: conv.fromId,
      fromName: conv.fromName,
      bookingId: thread.bookingId,
      propertyName: thread.propertyName,
      id: thread.messages[0]?.id || thread.messages[0]?._id,
    };
  }, [conversations, selectedUserKey, selectedBookingId]);

  useEffect(() => {
    let filtered = conversations;
    if (filter === 'unread') filtered = conversations.filter((c) => c.hasUnread);
    else if (filter === 'read') filtered = conversations.filter((c) => !c.hasUnread);
    setFilteredMessages(filtered);
  }, [filter, conversations]);

  useEffect(() => {
    if (selectedMessage?.bookingId) loadConversationThread(selectedMessage);
    else setConversationThread([]);
  }, [selectedMessage?.bookingId, selectedMessage?.fromId]);

  // When navigated from host notification click with openBookingId, auto-select that user and booking
  useEffect(() => {
    const openBookingId = location.state?.openBookingId;
    if (!openBookingId || conversations.length === 0) return;
    const want = String(openBookingId).trim().toLowerCase();
    for (const conv of conversations) {
      const thread = conv.threads.find((t) => String(t.bookingId || '').trim().toLowerCase() === want);
      if (thread) {
        setSelectedUserKey(conv.key);
        setSelectedBookingId(thread.bookingId);
        break;
      }
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [conversations, location.state?.openBookingId]);

  useEffect(() => {
    if (chatBodyRef.current && conversationThread.length > 0) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [conversationThread]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const list = await fetchHostMessages();
      setMessages(list || []);
    } catch (err) {
      console.error('Error loading messages:', err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationThread = async (message) => {
    if (!message?.bookingId) return;
    setLoadingThread(true);
    try {
      const thread = await fetchConversation(message.bookingId);
      setConversationThread(thread || []);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setConversationThread([]);
    } finally {
      setLoadingThread(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleMarkAsRead = async (message) => {
    const id = message?.id || message?._id;
    if (!id || message?.read) return;
    setMarkingId(id);
    try {
      await markMessageAsRead(id, hostId, 'host');
      setMessages((prev) =>
        prev.map((m) => ((m.id || m._id) === id ? { ...m, read: true } : m))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setMarkingId(null);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    const ok = await deleteMessageApi(messageId);
    if (ok) {
      setMessages((prev) => prev.filter((m) => (m.id || m._id) !== messageId));
      toast.success('Message deleted');
    } else {
      toast.error('Failed to delete message');
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;
    setSendingReply(true);
    try {
      const result = await sendHostToGuestMessage({
        hostId,
        guestId: selectedMessage.fromId,
        bookingId: selectedMessage.bookingId,
        propertyName: selectedMessage.propertyName,
        message: replyText.trim(),
        hostName,
      });
      if (result.success) {
        setReplyText('');
        await loadConversationThread(selectedMessage);
        toast.success('Reply sent');
      } else {
        toast.error(result.message || 'Failed to send reply');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  const isUserSelected = (conv) => selectedUserKey === conv.key;

  const handleSelectUser = (conv) => {
    setSelectedUserKey(conv.key);
    setSelectedBookingId(conv.threads[0]?.bookingId ?? null);
    if (conv.lastMessage && !conv.lastMessage.read) handleMarkAsRead(conv.lastMessage);
  };

  return (
    <div className="host-page host-messages-page">
      <HostHeader
        currentUser={currentUser}
        unreadNotifCount={unreadNotifCount}
        unreadMessageCount={unreadCount}
      />
      <div className="host-messages-page-content">
        <div className="host-messages-header">
          <button
            type="button"
            className="btn-back-host"
            onClick={() => navigate('/host')}
            aria-label="Back to host dashboard"
          >
            <FaArrowLeft />
            <span>Back to Host Dashboard</span>
          </button>
          <div className="host-messages-header-copy">
            <h1>Guest Messages</h1>
            <p>Reply to guests and manage booking conversations</p>
          </div>
        </div>

        <div className="host-messages-layout">
          {/* Left - Inbox: same format as host dashboard (1st format) */}
          <aside className="host-messages-inbox">
            <h2 className="host-messages-panel-title">Inbox</h2>
            {conversations.length > 0 && (
              <div className="host-messages-filters">
                <button
                  type="button"
                  className={`host-messages-filter-btn ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  All ({conversations.length})
                </button>
                <button
                  type="button"
                  className={`host-messages-filter-btn ${filter === 'unread' ? 'active' : ''}`}
                  onClick={() => setFilter('unread')}
                >
                  <FaEnvelope /> Unread ({conversations.filter((c) => c.hasUnread).length})
                </button>
                <button
                  type="button"
                  className={`host-messages-filter-btn ${filter === 'read' ? 'active' : ''}`}
                  onClick={() => setFilter('read')}
                >
                  <FaEnvelopeOpen /> Read ({conversations.filter((c) => !c.hasUnread).length})
                </button>
              </div>
            )}
            {loading ? (
              <div className="host-messages-panel-placeholder">
                <FaSpinner className="spinner" /> Loading...
              </div>
            ) : filteredMessages.length === 0 ? (
              <p className="host-messages-panel-placeholder">
                {filter === 'all'
                  ? 'Your message list will appear here.'
                  : filter === 'unread'
                  ? 'No unread messages.'
                  : 'No read messages.'}
              </p>
            ) : (
              <ul className="host-messages-list" aria-label="Conversations">
                {filteredMessages.map((conv) => {
                  const isSelected = isUserSelected(conv);
                  const rep = conv.lastMessage;
                  const repId = rep?.id || rep?._id;
                  const lastThread = conv.threads[0];
                  return (
                    <li key={conv.key}>
                      <div
                        className={`host-messages-card ${conv.hasUnread ? 'unread' : ''} ${isSelected ? 'selected' : ''}`}
                      >
                        <button
                          type="button"
                          className="host-messages-card-click"
                          onClick={() => handleSelectUser(conv)}
                          aria-label={`Open conversation with ${conv.fromName}`}
                        />
                        <div className="host-messages-card-header">
                          <span className="host-messages-avatar" aria-hidden="true">
                            {getInitials(conv.fromName)}
                          </span>
                          <strong>{conv.fromName}</strong>
                          <span className="host-messages-card-time">
                            <FaClock /> {formatTime(conv.lastTime)}
                          </span>
                          {conv.hasUnread && (
                            <span className="host-messages-unread-badge" aria-hidden>
                              {conv.unreadCount}
                            </span>
                          )}
                        </div>
                        <div className="host-messages-card-meta">
                          <span>Property: {lastThread?.propertyName || '—'}</span>
                          {conv.threads.length > 1 && (
                            <span className="host-messages-multi-booking">{conv.threads.length} bookings</span>
                          )}
                        </div>
                        <div className="host-messages-card-content">
                          <p>{rep?.message || '—'}</p>
                        </div>
                        <div className="host-messages-card-actions">
                          <button
                            type="button"
                            className="host-messages-btn-reply"
                            onClick={(e) => { e.stopPropagation(); handleSelectUser(conv); }}
                          >
                            <FaReply /> Reply
                          </button>
                          <button
                            type="button"
                            className="host-messages-btn-delete"
                            onClick={(e) => { e.stopPropagation(); if (repId) handleDelete(repId); }}
                            aria-label="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          {/* Center - Reply session: thread + reply input */}
          <section className="host-messages-center" aria-label="Conversation">
            {loading ? (
              <div className="host-messages-center-empty">
                <FaSpinner className="spinner" /> Loading...
              </div>
            ) : !selectedMessage ? (
              <div className="host-messages-center-empty">
                <FaComments className="host-messages-center-icon" />
                <p className="host-messages-center-text">Select a message to view and reply</p>
              </div>
            ) : (
              (() => {
                const selectedConv = conversations.find((c) => c.key === selectedUserKey);
                const hasMultipleBookings = selectedConv?.threads?.length > 1;
                return (
              <div className="host-messages-reply-session">
                <div className="host-messages-reply-header">
                  <div className="host-messages-reply-title-row">
                    <span className="host-messages-avatar host-messages-avatar--large" aria-hidden="true">
                      {getInitials(selectedMessage.fromName)}
                    </span>
                    <div>
                      <h3>Chat with {selectedMessage.fromName}</h3>
                  {hasMultipleBookings ? (
                    <div className="host-messages-booking-select-wrap">
                      <label htmlFor="host-messages-booking-select" className="host-messages-booking-select-label">Conversation:</label>
                      <select
                        id="host-messages-booking-select"
                        className="host-messages-booking-select"
                        value={String(selectedBookingId || '')}
                        onChange={(e) => setSelectedBookingId(e.target.value || null)}
                      >
                        {selectedConv.threads.map((t) => (
                          <option key={t.bookingId} value={String(t.bookingId)}>
                            {t.propertyName || 'Property'} — {String(t.bookingId).slice(-8)}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="host-messages-reply-meta">
                      <span><FaHome /> {selectedMessage.propertyName}</span>
                      <span>Booking: {selectedMessage.bookingId}</span>
                    </div>
                  )}
                    </div>
                  </div>
                </div>
                <div className="host-messages-chat-body" ref={chatBodyRef}>
                  {loadingThread ? (
                    <div className="host-messages-chat-loading">
                      <FaSpinner className="spinner" /> Loading conversation...
                    </div>
                  ) : conversationThread.length === 0 ? (
                    <div className="host-messages-chat-empty">
                      <p>No messages in this thread yet. Type a reply below.</p>
                    </div>
                  ) : (
                    conversationThread.map((m) => (
                      <div
                        key={m.id || m._id}
                        className={`host-messages-bubble host-messages-bubble--${m.from}`}
                      >
                        <p className="host-messages-bubble-text">{m.message}</p>
                        <span className="host-messages-bubble-time">{formatTime(m.timestamp)}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className="host-messages-chat-input-bar">
                  <div className="host-messages-composer">
                    <input
                      type="text"
                      className="host-messages-chat-input"
                      placeholder="Type your reply..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      disabled={sendingReply}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSendReply();
                        }
                      }}
                      aria-label="Reply"
                    />
                    <button
                      type="button"
                      className="host-messages-btn-send"
                      onClick={handleSendReply}
                      disabled={sendingReply || !replyText.trim()}
                      title="Send"
                    >
                      {sendingReply ? <FaSpinner className="spinner" /> : <FaPaperPlane />}
                    </button>
                  </div>
                </div>
              </div>
                );
              })()
            )}
          </section>

          {/* Right - Booking Details */}
          <aside className="host-messages-booking">
            <h2 className="host-messages-panel-title">Booking Details</h2>
            {!selectedMessage ? (
              <p className="host-messages-panel-placeholder">
                Your booking details will appear here when you select a message.
              </p>
            ) : (
              <div className="host-messages-booking-details">
                <div className="host-messages-booking-card">
                  <div className="host-messages-booking-row">
                    <span className="host-messages-booking-label">Guest</span>
                    <span className="host-messages-booking-value">{selectedMessage.fromName}</span>
                  </div>
                  {selectedMessage.propertyName && (
                    <div className="host-messages-booking-row">
                      <span className="host-messages-booking-label">Property</span>
                      <span className="host-messages-booking-value">
                        {selectedMessage.propertyName}
                      </span>
                    </div>
                  )}
                  {selectedMessage.bookingId && (
                    <div className="host-messages-booking-row">
                      <span className="host-messages-booking-label">Booking ID</span>
                      <span className="host-messages-booking-value host-messages-booking-id">
                        {selectedMessage.bookingId}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default HostMessagesPage;
